import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Select,
  MenuItem,
  TextField,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip as MuiTooltip,
  Menu,
  InputAdornment,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CloseIcon from '@mui/icons-material/Close';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import * as XLSX from 'xlsx';
import { InsightsPanel, NLQueryBar, ForecastTab, CustomerSegmentTab, RecommendationsPanel } from './SalesDashboardAI';

const US_TOPO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// Coordinates are nudged from actual locations to prevent overlap on the map
const WAREHOUSE_LOCATIONS = {
  L10: { name: 'Port Washington, NY', lat: 41.4, lng: -72.5, region: 'Northeast' },
  L21: { name: 'Easton, PA', lat: 41.8, lng: -75.2, region: 'Northeast' },
  L28: { name: 'Easton, PA', lat: 40.0, lng: -75.8, region: 'Northeast' },
  L29: { name: 'Carlisle, PA', lat: 39.2, lng: -77.6, region: 'Northeast' },
  L40: { name: 'Lebanon, NJ', lat: 42.3, lng: -73.5, region: 'Northeast' },
  L41: { name: 'Flemington, NJ', lat: 40.7, lng: -73.2, region: 'Northeast' },
  L35: { name: 'Savannah, GA', lat: 33.0, lng: -82.0, region: 'Southeast' },
  L36: { name: 'Port Wentworth, GA', lat: 31.5, lng: -82.5, region: 'Southeast' },
  L37: { name: 'Midway, GA', lat: 32.2, lng: -80.2, region: 'Southeast' },
  L55: { name: 'Whitestown, IN', lat: 40.8, lng: -86.0, region: 'Midwest' },
  L56: { name: 'Whitestown, IN', lat: 39.2, lng: -86.8, region: 'Midwest' },
  L57: { name: 'Joliet, IL', lat: 41.5, lng: -88.1, region: 'Midwest' },
  L60: { name: 'Riverside, CA', lat: 33.9, lng: -117.4, region: 'West' },
  L65: { name: 'Patterson, CA', lat: 37.5, lng: -121.1, region: 'West' },
  L70: { name: 'Baytown, TX', lat: 29.7, lng: -95.0, region: 'Southwest' },
  L91: { name: 'Morrisburg, ON (Canada)', lat: 44.9, lng: -75.19, region: 'Northeast' },
};

const REGION_COLORS = {
  Northeast: '#7C6F8A',
  Southeast: '#6B8F71',
  Midwest: '#8B7355',
  West: '#5B8FA6',
  Southwest: '#A67B5B',
  Central: '#888',
};

// US state center coordinates for zip code mapping
const STATE_COORDS = {
  AL:[32.8,-86.8],AK:[64.2,-152.5],AZ:[34.0,-111.1],AR:[35.2,-91.8],CA:[36.8,-119.4],
  CO:[39.1,-105.4],CT:[41.6,-72.7],DE:[39.0,-75.5],FL:[27.8,-81.7],GA:[32.2,-83.1],
  HI:[19.9,-155.6],ID:[44.1,-114.7],IL:[40.3,-89.0],IN:[40.3,-86.1],IA:[42.0,-93.2],
  KS:[38.5,-98.3],KY:[37.8,-84.3],LA:[30.9,-92.3],ME:[45.3,-69.4],MD:[39.0,-76.6],
  MA:[42.4,-71.4],MI:[44.3,-85.6],MN:[46.7,-94.7],MS:[32.7,-89.7],MO:[38.5,-91.8],
  MT:[46.8,-110.4],NE:[41.5,-99.9],NV:[38.8,-116.4],NH:[43.2,-71.6],NJ:[40.1,-74.5],
  NM:[34.5,-105.9],NY:[43.0,-75.0],NC:[35.8,-80.8],ND:[47.5,-100.5],OH:[40.4,-82.9],
  OK:[35.0,-97.1],OR:[43.8,-120.6],PA:[41.2,-77.2],RI:[41.6,-71.5],SC:[33.8,-81.2],
  SD:[43.9,-99.4],TN:[35.5,-86.6],TX:[31.0,-100.0],UT:[39.3,-111.1],VT:[44.6,-72.6],
  VA:[37.4,-79.5],WA:[47.8,-120.7],WV:[38.5,-80.5],WI:[43.8,-89.5],WY:[43.1,-107.6],
  DC:[38.9,-77.0],PR:[18.2,-66.6],
};

const STATE_NAME_TO_ABBREV = {Alabama:'AL',Alaska:'AK',Arizona:'AZ',Arkansas:'AR',California:'CA',Colorado:'CO',Connecticut:'CT',Delaware:'DE',Florida:'FL',Georgia:'GA',Hawaii:'HI',Idaho:'ID',Illinois:'IL',Indiana:'IN',Iowa:'IA',Kansas:'KS',Kentucky:'KY',Louisiana:'LA',Maine:'ME',Maryland:'MD',Massachusetts:'MA',Michigan:'MI',Minnesota:'MN',Mississippi:'MS',Missouri:'MO',Montana:'MT',Nebraska:'NE',Nevada:'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',Ohio:'OH',Oklahoma:'OK',Oregon:'OR',Pennsylvania:'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD',Tennessee:'TN',Texas:'TX',Utah:'UT',Vermont:'VT',Virginia:'VA',Washington:'WA','West Virginia':'WV',Wisconsin:'WI',Wyoming:'WY','District of Columbia':'DC'};

const MapTooltip = ({ data, position }) => {
  if (!data || !position) return null;
  return (
    <Box
      sx={{
        position: 'fixed',
        left: position.x + 12,
        top: position.y - 10,
        zIndex: 9999,
        bgcolor: 'rgba(20,20,30,0.95)',
        color: '#fff',
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        pointerEvents: 'none',
        minWidth: 160,
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        border: '1px solid rgba(255,255,255,0.15)',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.3, color: '#e0d4f0' }}>{data.title}</Typography>
      {data.subtitle && <Typography sx={{ fontSize: 11, color: '#aaa', mb: 0.5 }}>{data.subtitle}</Typography>}
      {data.rows && data.rows.map((row, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={{ fontSize: 11, color: '#bbb' }}>{row.label}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: row.color || '#fff' }}>{row.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const REPORT_TABS = [
  'Top SKUs',
  'Top Customers',
  'Top Collections',
  'Top Sizes',
  'Top Colors',
  'Top Styles',
  'Top Warehouses',
  'Top Zip Codes',
  'Items by Zip',
  'AI Forecast',
  'AI Customers',
];

const REPORT_FILE_NAMES = [
  'Top_SKUs',
  'Top_Customers',
  'Top_Collections',
  'Top_Sizes',
  'Top_Colors',
  'Top_Styles',
  'Top_Warehouses',
  'Top_ZipCodes',
  'Items_By_Zip',
  'AI_Forecast',
  'AI_Customers',
];

const TOP_N_OPTIONS = [
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 250, label: '250' },
  { value: 500, label: '500' },
  { value: 0, label: 'All' },
];

const PIE_COLORS = [
  '#9C8EA4', '#6B8F71', '#E8927C', '#7BA7BC', '#D4A574',
  '#A8C5DA', '#C4956A', '#8FBC8F', '#CD853F', '#B0A4B8',
];

const fmtDollar = (v) => '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (v) => v.toLocaleString('en-US');

const formatDate = (d) => d.toISOString().slice(0, 10);
const getDefaultDateFrom = () => { const d = new Date(); d.setDate(d.getDate() - 7); return formatDate(d); };
const getDefaultDateTo = () => formatDate(new Date());

const SalesDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Data state
  const [dsData, setDsData] = useState([]);
  const [nonDsData, setNonDsData] = useState([]);
  const [itemsLookup, setItemsLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemsLoaded, setItemsLoaded] = useState(false);

  // Filter state - default last 7 days
  const [source, setSource] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom());
  const [dateTo, setDateTo] = useState(getDefaultDateTo());
  const [topN, setTopN] = useState(100);
  const [search, setSearch] = useState('');
  const [customerFilters, setCustomerFilters] = useState([]);
  const [customerInput, setCustomerInput] = useState('');
  const [collectionFilters, setCollectionFilters] = useState([]);
  const [collectionInput, setCollectionInput] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [drillDownState, setDrillDownState] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [sortCol, setSortCol] = useState(-1);
  const [sortDir, setSortDir] = useState('desc');

  // View mode: 'table', 'chart', 'pie'
  const [viewMode, setViewMode] = useState('table');

  // Export menu anchor
  const [exportAnchor, setExportAnchor] = useState(null);

  // Map tooltip state
  const [mapTooltip, setMapTooltip] = useState(null);
  const [mapTooltipPos, setMapTooltipPos] = useState(null);

  // AI recommendations state
  const [aiSelectedSku, setAiSelectedSku] = useState(null);
  const [aiSelectedCustomer, setAiSelectedCustomer] = useState(null);

  // Warehouse comparison selection
  const [selectedWarehouses, setSelectedWarehouses] = useState(new Set());
  const toggleWarehouse = (code) => {
    setSelectedWarehouses(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  // Fetch data from backend - always loads both DS and NonDS, source filtering is client-side
  const fetchData = useCallback(async (fromOverride, toOverride) => {
    setLoading(true);
    setError(null);
    try {
      const from = fromOverride ?? dateFrom;
      const to = toOverride ?? dateTo;

      const params = new URLSearchParams();
      if (from) params.append('dateFrom', from);
      if (to) params.append('dateTo', to);

      const queryString = params.toString();
      const dataUrl = API_ENDPOINTS.SALES_DASHBOARD_DATA + (queryString ? `?${queryString}` : '');

      // Load items lookup only once
      const promises = [apiCall(dataUrl)];
      if (!itemsLoaded) promises.push(apiCall(API_ENDPOINTS.SALES_DASHBOARD_ITEMS_LOOKUP));

      const results = await Promise.all(promises);
      const dataResult = results[0];

      const parseDates = (rows) =>
        (rows || []).map((r) => ({
          ...r,
          shipDate: r.shipDate ? new Date(r.shipDate + 'T00:00:00') : null,
        }));

      setDsData(parseDates(dataResult.dsData));
      setNonDsData(parseDates(dataResult.nonDsData));

      if (results[1]) {
        setItemsLookup(results[1].items || {});
        setItemsLoaded(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, itemsLoaded]);

  // Load on mount with default dates
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  // eslint-disable-next-line
  }, []);

  // Reset all filters to defaults and reload
  const handleReset = useCallback(() => {
    const from = getDefaultDateFrom();
    const to = getDefaultDateTo();
    setSource('all');
    setMonthFilter('all');
    setDateFrom(from);
    setDateTo(to);
    setSearch('');
    setTopN(100);
    setCurrentTab(0);
    setSortCol(-1);
    setViewMode('table');
    setCustomerFilters([]);
    setCustomerInput('');
    setCollectionFilters([]);
    setCollectionInput('');
    setCompareMode(false);
    fetchData(from, to);
  }, [fetchData]);

  // Item info lookup helper
  const getItemInfo = useCallback(
    (itemId) => {
      if (itemsLookup[itemId]) return itemsLookup[itemId];
      const base = itemId.replace(/-[^-]+$/, '');
      if (base !== itemId && itemsLookup[base]) return itemsLookup[base];
      return { collection: '', color: '', size: '', style: '' };
    },
    [itemsLookup]
  );

  // Compute available months from data
  const availableMonths = useMemo(() => {
    const all = dsData.concat(nonDsData);
    const months = new Set();
    for (const r of all) {
      if (r.shipDate) {
        const key = r.shipDate.getFullYear() + '-' + String(r.shipDate.getMonth() + 1).padStart(2, '0');
        months.add(key);
      }
    }
    return [...months].sort().reverse();
  }, [dsData, nonDsData]);

  // Date range text
  const dateRangeText = useMemo(() => {
    const all = dsData.concat(nonDsData);
    const dates = all.filter((r) => r.shipDate).map((r) => r.shipDate).sort((a, b) => a - b);
    if (dates.length === 0) return 'No data loaded';
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `Data: ${fmt(dates[0])} - ${fmt(dates[dates.length - 1])}`;
  }, [dsData, nonDsData]);

  // Filtered data
  const filteredData = useMemo(() => {
    let rows = [];
    if (source === 'all' || source === 'ds') rows = rows.concat(dsData);
    if (source === 'all' || source === 'nonDs') rows = rows.concat(nonDsData);

    if (monthFilter !== 'all') {
      const [y, m] = monthFilter.split('-').map(Number);
      rows = rows.filter((r) => r.shipDate && r.shipDate.getFullYear() === y && r.shipDate.getMonth() === m - 1);
    }
    // Date filtering is done server-side, no need to filter again here
    const allCustomerTerms = [...customerFilters.map(t => t.toLowerCase()), ...(customerInput.trim() ? [customerInput.trim().toLowerCase()] : [])];
    if (allCustomerTerms.length > 0) {
      rows = rows.filter((r) => r.customer && allCustomerTerms.some(t => r.customer.toLowerCase().includes(t)));
    }
    const allCollectionTerms = [...collectionFilters.map(t => t.toLowerCase()), ...(collectionInput.trim() ? [collectionInput.trim().toLowerCase()] : [])];
    if (allCollectionTerms.length > 0) {
      rows = rows.filter((r) => {
        const info = getItemInfo(r.itemId);
        return info.collection && allCollectionTerms.some(t => info.collection.toLowerCase().includes(t));
      });
    }
    return rows;
  }, [dsData, nonDsData, source, monthFilter, dateFrom, dateTo, customerFilters, customerInput, collectionFilters, collectionInput, getItemInfo]);

  // Previous period data for comparison
  const previousPeriodData = useMemo(() => {
    if (!compareMode || !dateFrom || !dateTo) return null;
    const from = new Date(dateFrom + 'T00:00:00');
    const to = new Date(dateTo + 'T23:59:59');
    const rangeDays = Math.max(1, Math.round((to - from) / 86400000));
    const prevTo = new Date(from.getTime() - 86400000); // day before current from
    const prevFrom = new Date(prevTo.getTime() - rangeDays * 86400000);

    let rows = [];
    if (source === 'all' || source === 'ds') rows = rows.concat(dsData);
    if (source === 'all' || source === 'nonDs') rows = rows.concat(nonDsData);
    rows = rows.filter(r => r.shipDate && r.shipDate >= prevFrom && r.shipDate <= prevTo);
    const allCustTerms = [...customerFilters.map(t => t.toLowerCase()), ...(customerInput.trim() ? [customerInput.trim().toLowerCase()] : [])];
    if (allCustTerms.length > 0) {
      rows = rows.filter(r => r.customer && allCustTerms.some(t => r.customer.toLowerCase().includes(t)));
    }
    const allColTerms = [...collectionFilters.map(t => t.toLowerCase()), ...(collectionInput.trim() ? [collectionInput.trim().toLowerCase()] : [])];
    if (allColTerms.length > 0) {
      rows = rows.filter(r => { const info = getItemInfo(r.itemId); return info.collection && allColTerms.some(t => info.collection.toLowerCase().includes(t)); });
    }
    const prevLabel = `${prevFrom.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${prevTo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    return { rows, rangeDays, prevFrom, prevTo, prevLabel };
  }, [compareMode, dateFrom, dateTo, dsData, nonDsData, source, customerFilters, customerInput, collectionFilters, collectionInput, getItemInfo]);

  // Number of days in current date range (for velocity)
  const currentRangeDays = useMemo(() => {
    if (!dateFrom || !dateTo) return 7;
    return Math.max(1, Math.round((new Date(dateTo + 'T23:59:59') - new Date(dateFrom + 'T00:00:00')) / 86400000));
  }, [dateFrom, dateTo]);

  // DS vs Non-DS comparison data
  const dsComparison = useMemo(() => {
    let dsRows = [...dsData];
    let nonDsRows = [...nonDsData];

    // Apply same date/month filters
    if (monthFilter !== 'all') {
      const [y, m] = monthFilter.split('-').map(Number);
      dsRows = dsRows.filter((r) => r.shipDate && r.shipDate.getFullYear() === y && r.shipDate.getMonth() === m - 1);
      nonDsRows = nonDsRows.filter((r) => r.shipDate && r.shipDate.getFullYear() === y && r.shipDate.getMonth() === m - 1);
    }
    if (dateFrom) {
      const from = new Date(dateFrom + 'T00:00:00');
      dsRows = dsRows.filter((r) => r.shipDate && r.shipDate >= from);
      nonDsRows = nonDsRows.filter((r) => r.shipDate && r.shipDate >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      dsRows = dsRows.filter((r) => r.shipDate && r.shipDate <= to);
      nonDsRows = nonDsRows.filter((r) => r.shipDate && r.shipDate <= to);
    }

    const dsAmt = dsRows.reduce((s, r) => s + (r.amount || 0), 0);
    const nonDsAmt = nonDsRows.reduce((s, r) => s + (r.amount || 0), 0);
    const total = dsAmt + nonDsAmt;
    return {
      dsAmt,
      nonDsAmt,
      total,
      dsPct: total > 0 ? ((dsAmt / total) * 100).toFixed(1) : '0.0',
      nonDsPct: total > 0 ? ((nonDsAmt / total) * 100).toFixed(1) : '0.0',
    };
  }, [dsData, nonDsData, monthFilter, dateFrom, dateTo]);

  // Report generators
  const reportData = useMemo(() => {
    const rows = filteredData;
    const s = search.toLowerCase();
    const prevRows = previousPeriodData?.rows;
    const days = currentRangeDays;

    switch (currentTab) {
      case 0: return reportTopSKUs(rows, s, getItemInfo, prevRows, days);
      case 1: return reportTopCustomers(rows, s, prevRows);
      case 2: return reportTopCollections(rows, s, getItemInfo, prevRows);
      case 3: return reportTopSizes(rows, s, getItemInfo, prevRows);
      case 4: return reportTopColors(rows, s, getItemInfo, prevRows);
      case 5: return reportTopStyles(rows, s, getItemInfo, prevRows);
      case 6: return reportTopWarehouses(rows, s, days, prevRows);
      case 7: return reportTopZips(rows, s, prevRows, drillDownState);
      case 8: return reportItemsByZip(rows, s, getItemInfo);
      default: return null;
    }
  }, [filteredData, search, currentTab, getItemInfo, previousPeriodData, currentRangeDays, drillDownState]);

  // Set default sort to Amount column (last sortable column, descending) when tab changes
  useEffect(() => {
    if (reportData && reportData.headers) {
      // Find the last sortable column (Amount is always last)
      const amtIdx = reportData.headers.length - 1;
      setSortCol(amtIdx);
      setSortDir('desc');
    }
  }, [currentTab, reportData]);

  // Apply sorting and topN to displayed rows
  const displayedRows = useMemo(() => {
    if (!reportData) return [];
    let rows = [...reportData.tableRows];
    const raw = reportData.rawData;

    // Sort if a column is selected, with secondary sort on the other numeric column
    if (sortCol >= 0 && raw && raw.length === rows.length) {
      // Find the other sortable column for secondary sort
      const sortableIndices = (reportData.headers || [])
        .map((h, i) => h.sortable ? i : -1).filter(i => i >= 0);
      const secondaryCol = sortableIndices.find(i => i !== sortCol) ?? -1;

      const indices = rows.map((_, i) => i);
      indices.sort((a, b) => {
        const va = raw[a][sortCol] ?? 0;
        const vb = raw[b][sortCol] ?? 0;
        const diff = sortDir === 'asc' ? va - vb : vb - va;
        if (diff !== 0) return diff;
        // Secondary sort: same direction as primary
        if (secondaryCol >= 0) {
          const sa = raw[a][secondaryCol] ?? 0;
          const sb = raw[b][secondaryCol] ?? 0;
          return sortDir === 'asc' ? sa - sb : sb - sa;
        }
        return 0;
      });
      rows = indices.map(i => rows[i]);
    }

    if (topN > 0) return rows.slice(0, topN);
    return rows;
  }, [reportData, topN, sortCol, sortDir]);

  // Chart data derived from displayedRows
  const chartData = useMemo(() => {
    if (!reportData || displayedRows.length === 0) return [];
    const headers = reportData.headers;
    // Find the Amount column index (last header)
    const amtIdx = headers.length - 1;
    // First column is the label
    const labelIdx = 0;

    return displayedRows.slice(0, 25).map((row) => {
      // Parse the amount value back to number
      const amtStr = String(row[amtIdx]).replace(/[$,]/g, '');
      const amt = parseFloat(amtStr) || 0;
      const label = String(row[labelIdx]);
      // Truncate long labels for chart readability
      const shortLabel = label.length > 20 ? label.slice(0, 18) + '...' : label;
      return { name: shortLabel, fullName: label, Amount: amt };
    });
  }, [reportData, displayedRows]);

  // Pie chart data - top 10
  const pieData = useMemo(() => {
    if (!reportData || displayedRows.length === 0) return [];
    const headers = reportData.headers;
    const amtIdx = headers.length - 1;
    const labelIdx = 0;

    const items = displayedRows.slice(0, 10).map((row) => {
      const amtStr = String(row[amtIdx]).replace(/[$,]/g, '');
      const amt = parseFloat(amtStr) || 0;
      const label = String(row[labelIdx]);
      const shortLabel = label.length > 25 ? label.slice(0, 23) + '...' : label;
      return { name: shortLabel, fullName: label, value: amt };
    });
    const total = items.reduce((s, i) => s + i.value, 0);
    return items.map((i) => ({ ...i, pct: total > 0 ? ((i.value / total) * 100).toFixed(1) : '0.0' }));
  }, [reportData, displayedRows]);

  // Build CSV string (reused for export and copy)
  const buildCSV = useCallback(() => {
    if (!reportData || displayedRows.length === 0) return '';
    const { headers } = reportData;
    let csv = '';
    csv += '"#",' + headers.map((h) => `"${h.label}"`).join(',') + '\n';
    displayedRows.forEach((row, idx) => {
      const cells = row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`);
      csv += `"${idx + 1}",` + cells.join(',') + '\n';
    });
    return csv;
  }, [reportData, displayedRows]);

  // Export CSV
  const handleExportCSV = useCallback(() => {
    const csv = buildCSV();
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = REPORT_FILE_NAMES[currentTab] + '_' + new Date().toISOString().slice(0, 10) + '.csv';
    a.click();
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  }, [buildCSV, currentTab]);

  // Export Excel with proper XLSX (Sheet 1: summary, Sheet 2: SKU details for Top Customers)
  const handleExportExcel = useCallback(() => {
    if (!reportData || displayedRows.length === 0) return;
    const wb = XLSX.utils.book_new();

    // Sheet 1: main report table
    const sheet1Data = [['#', ...reportData.headers.map(h => h.label)]];
    displayedRows.forEach((row, idx) => {
      sheet1Data.push([idx + 1, ...row]);
    });
    const ws1 = XLSX.utils.aoa_to_sheet(sheet1Data);
    XLSX.utils.book_append_sheet(wb, ws1, reportData.title || 'Report');

    // Sheet 2: SKU/Item details per customer (only for Top Customers tab = currentTab 1)
    if (currentTab === 1) {
      const detailRows = [['Customer', 'SKU', 'Collection', 'Color', 'Size', 'Style', 'Units', 'Amount']];
      // Build per-customer per-SKU breakdown from filteredData
      const custSkuMap = {};
      for (const r of filteredData) {
        if (!r.customer) continue;
        const key = `${r.customer}|||${r.itemId}`;
        if (!custSkuMap[key]) custSkuMap[key] = { customer: r.customer, itemId: r.itemId, qty: 0, amount: 0 };
        custSkuMap[key].qty += r.qty;
        custSkuMap[key].amount += r.amount;
      }
      // Only include customers that are in the displayed report (respects search + topN)
      const displayedCustomers = new Set(displayedRows.map(row => row[0]));
      const details = Object.values(custSkuMap)
        .filter(d => displayedCustomers.has(d.customer))
        .sort((a, b) => a.customer.localeCompare(b.customer) || b.amount - a.amount);
      for (const d of details) {
        const info = getItemInfo(d.itemId);
        detailRows.push([d.customer, d.itemId, info.collection || '', info.color || '', info.size || '', info.style || '', d.qty, d.amount]);
      }
      const ws2 = XLSX.utils.aoa_to_sheet(detailRows);
      XLSX.utils.book_append_sheet(wb, ws2, 'SKU Details by Customer');
    }

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = REPORT_FILE_NAMES[currentTab] + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
    a.click();
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  }, [reportData, displayedRows, currentTab, filteredData, getItemInfo]);

  // Copy to clipboard
  const handleCopyToClipboard = useCallback(() => {
    const csv = buildCSV();
    if (!csv) return;
    navigator.clipboard.writeText(csv).catch(() => {});
  }, [buildCSV]);

  const cardBg = getCardBackground(theme);
  const isDark = theme.palette.mode === 'dark';

  // Custom tooltip for recharts
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{
          bgcolor: isDark ? '#2a2a2a' : '#fff',
          border: `1px solid ${isDark ? 'rgba(47,196,178,0.3)' : 'rgba(47,196,178,0.2)'}`,
          borderRadius: 1,
          p: 1.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: isDark ? '#e0e0e0' : '#333', mb: 0.5 }}>
            {payload[0]?.payload?.fullName || label}
          </Typography>
          <Typography sx={{ fontSize: 13, color: TOOL_COLORS.REPORTS.primary, fontWeight: 700 }}>
            {fmtDollar(payload[0].value)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <Box sx={{
          bgcolor: isDark ? '#2a2a2a' : '#fff',
          border: `1px solid ${isDark ? 'rgba(47,196,178,0.3)' : 'rgba(47,196,178,0.2)'}`,
          borderRadius: 1,
          p: 1.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: isDark ? '#e0e0e0' : '#333', mb: 0.5 }}>
            {d.fullName}
          </Typography>
          <Typography sx={{ fontSize: 13, color: TOOL_COLORS.REPORTS.primary, fontWeight: 700 }}>
            {fmtDollar(d.value)} ({d.pct}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderPieLabel = ({ name, pct, cx, x }) => {
    const anchor = x > cx ? 'start' : 'end';
    return (
      <text x={x} y={undefined} textAnchor={anchor} fill={isDark ? '#ccc' : '#555'} fontSize={11}>
        {name} ({pct}%)
      </text>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ position: 'relative', textAlign: 'center', mb: 3 }}>
        <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
          <FavoriteButton interfaceName="Sales Dashboard" category="reports" />
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: '1px',
            color: TOOL_COLORS.REPORTS.primary,
            pb: 1.5,
          }}
        >
          SALES DASHBOARD
        </Typography>
        <Box sx={{ width: 60, height: 3, bgcolor: TOOL_COLORS.REPORTS.primary, mx: 'auto', borderRadius: 2, mb: 0.5 }} />
        <Typography variant="caption" color="text.secondary">
          Real-time sales analytics and performance insights
        </Typography>
      </Box>

      {/* Filter Panel */}
      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mb: 2, overflow: 'hidden', bgcolor: cardBg }}>
        {/* Row 1: Source + Date Range + Month */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${theme.palette.divider}` }}>
          {/* Source */}
          <ToggleButtonGroup
            value={source}
            exclusive
            onChange={(e, val) => { if (val) setSource(val); }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2, py: 0.5, fontSize: 12, fontWeight: 600,
                border: `1px solid ${TOOL_COLORS.REPORTS.primary}`,
                color: TOOL_COLORS.REPORTS.primary,
                textTransform: 'none',
                '&.Mui-selected': { bgcolor: TOOL_COLORS.REPORTS.primary, color: 'white', '&:hover': { bgcolor: TOOL_COLORS.REPORTS.hover } },
                '&:hover': { bgcolor: isDark ? 'rgba(47,196,178,0.1)' : 'rgba(47,196,178,0.08)' },
              },
            }}
          >
            <ToggleButton value="all">All Orders</ToggleButton>
            <ToggleButton value="ds">DS</ToggleButton>
            <ToggleButton value="nonDs">Non DS</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ width: '1px', height: 28, bgcolor: theme.palette.divider }} />

          {/* Date Range */}
          <TextField
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); fetchData(e.target.value, dateTo); }}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '48%', sm: 160 }, '& .MuiInputBase-input': { fontSize: 13 }, '& .MuiInputLabel-root': { fontSize: 13 } }}
          />
          <TextField
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); fetchData(dateFrom, e.target.value); }}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '48%', sm: 160 }, '& .MuiInputBase-input': { fontSize: 13 }, '& .MuiInputLabel-root': { fontSize: 13 } }}
          />

          {/* Quick Date Presets */}
          {[
            { label: 'Today', days: 0 },
            { label: '3 Days', days: 3 },
            { label: '1 Week', days: 7 },
            { label: '2 Weeks', days: 14 },
            { label: '1 Month', days: 30 },
          ].map(({ label, days }) => {
            const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const today = localDate(new Date());
            const fromDate = new Date(); fromDate.setDate(fromDate.getDate() - days);
            const from = days === 0 ? today : localDate(fromDate);
            const to = today;
            const isActive = dateFrom === from && dateTo === to;
            return (
              <Chip key={label} label={label} size="small" clickable
                onClick={() => { setDateFrom(from); setDateTo(to); fetchData(from, to); }}
                sx={{
                  height: 24, fontSize: '0.7rem', fontWeight: isActive ? 700 : 400,
                  bgcolor: isActive ? 'rgba(47,196,178,0.15)' : 'transparent',
                  border: `1px solid ${isActive ? TOOL_COLORS.REPORTS.primary : theme.palette.divider}`,
                  color: isActive ? TOOL_COLORS.REPORTS.primary : theme.palette.text.secondary,
                  '&:hover': { bgcolor: 'rgba(47,196,178,0.08)' },
                }}
              />
            );
          })}

          <Box sx={{ width: '1px', height: 28, bgcolor: theme.palette.divider }} />

          {/* Month */}
          <Select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            size="small"
            displayEmpty
            sx={{ fontSize: 13, minWidth: 150 }}
          >
            <MenuItem value="all">All Months</MenuItem>
            {availableMonths.map((m) => {
              const [y, mo] = m.split('-');
              const label = new Date(y, mo - 1, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
              return <MenuItem key={m} value={m}>{label}</MenuItem>;
            })}
          </Select>

          <Box sx={{ width: '1px', height: 28, bgcolor: theme.palette.divider }} />

          {/* Customer Filter — add one by one */}
          <TextField
            value={customerInput}
            onChange={(e) => setCustomerInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customerInput.trim()) {
                const val = customerInput.trim();
                if (!customerFilters.includes(val)) setCustomerFilters(prev => [...prev, val]);
                setCustomerInput('');
              }
            }}
            placeholder="Add customer..."
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><PersonSearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: customerInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => {
                    const val = customerInput.trim();
                    if (val && !customerFilters.includes(val)) setCustomerFilters(prev => [...prev, val]);
                    setCustomerInput('');
                  }} sx={{ p: 0.25, color: TOOL_COLORS.REPORTS.primary }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>+</Typography>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 200, '& .MuiInputBase-input': { fontSize: 12 } }}
          />
          {customerFilters.map((c, i) => (
            <Chip key={i} label={c} size="small"
              onDelete={() => setCustomerFilters(prev => prev.filter((_, j) => j !== i))}
              sx={{ fontSize: 11, fontWeight: 600, bgcolor: isDark ? 'rgba(156,142,164,0.15)' : 'rgba(107,79,125,0.12)', color: isDark ? TOOL_COLORS.REPORTS.primary : '#5B3E6F', border: `1px solid ${isDark ? 'rgba(156,142,164,0.3)' : 'rgba(107,79,125,0.3)'}` }} />
          ))}
          {customerFilters.length > 1 && (
            <Chip label="Clear all" size="small" clickable onClick={() => setCustomerFilters([])}
              sx={{ fontSize: 10, height: 20, color: 'text.secondary' }} />
          )}

          {/* Collection Filter — add one by one */}
          <TextField
            value={collectionInput}
            onChange={(e) => setCollectionInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && collectionInput.trim()) {
                const val = collectionInput.trim();
                if (!collectionFilters.includes(val)) setCollectionFilters(prev => [...prev, val]);
                setCollectionInput('');
              }
            }}
            placeholder="Add collection..."
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Inventory2OutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: collectionInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => {
                    const val = collectionInput.trim();
                    if (val && !collectionFilters.includes(val)) setCollectionFilters(prev => [...prev, val]);
                    setCollectionInput('');
                  }} sx={{ p: 0.25, color: '#2DD4BF' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>+</Typography>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 200, '& .MuiInputBase-input': { fontSize: 12 } }}
          />
          {collectionFilters.map((c, i) => (
            <Chip key={i} label={c} size="small"
              onDelete={() => setCollectionFilters(prev => prev.filter((_, j) => j !== i))}
              sx={{ fontSize: 11, fontWeight: 600, bgcolor: isDark ? 'rgba(45,212,191,0.15)' : 'rgba(16,130,112,0.12)', color: isDark ? '#2DD4BF' : '#0E7A63', border: `1px solid ${isDark ? 'rgba(45,212,191,0.3)' : 'rgba(16,130,112,0.3)'}` }} />
          ))}
          {collectionFilters.length > 1 && (
            <Chip label="Clear all" size="small" clickable onClick={() => setCollectionFilters([])}
              sx={{ fontSize: 10, height: 20, color: 'text.secondary' }} />
          )}

          {/* Compare Toggle */}
          <Chip
            label={compareMode ? `Compare: ON (vs ${previousPeriodData?.prevLabel || 'prev'})` : 'Compare Periods'}
            size="small"
            clickable
            onClick={() => setCompareMode(!compareMode)}
            sx={{
              fontSize: 11, fontWeight: compareMode ? 700 : 400,
              bgcolor: compareMode ? 'rgba(45,212,191,0.15)' : 'transparent',
              border: `1px solid ${compareMode ? '#2DD4BF' : theme.palette.divider}`,
              color: compareMode ? '#2DD4BF' : theme.palette.text.secondary,
            }}
          />

          {/* Date Range Chip */}
          <Chip
            label={dateRangeText}
            size="small"
            sx={{ fontSize: 11, fontWeight: 600, bgcolor: isDark ? 'rgba(47,196,178,0.15)' : 'rgba(47,196,178,0.08)', color: TOOL_COLORS.REPORTS.primary }}
          />
        </Box>

        {/* Row 2: Search + Top N + Actions */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            size="small"
            sx={{ width: 220, '& .MuiInputBase-input': { fontSize: 13, py: '6px' } }}
          />

          <Select value={topN} onChange={(e) => setTopN(e.target.value)} size="small" sx={{ fontSize: 12, minWidth: 80 }}>
            {TOP_N_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>Top {opt.label}</MenuItem>
            ))}
          </Select>

          <Box sx={{ flex: 1 }} />

          {/* Copy button */}
          <MuiTooltip title="Copy to clipboard">
            <IconButton onClick={handleCopyToClipboard} size="small"
              sx={{ color: TOOL_COLORS.REPORTS.primary, border: `1px solid ${TOOL_COLORS.REPORTS.primary}`, borderRadius: 1, p: '5px' }}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </MuiTooltip>

          {/* Export dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExportExcel} size="small"
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: 12, fontWeight: 600, textTransform: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
              Export
            </Button>
            <Button variant="contained" size="small" onClick={(e) => setExportAnchor(e.currentTarget)}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, minWidth: 28, px: 0.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: '1px solid rgba(255,255,255,0.3)' }}>
              <ArrowDropDownIcon sx={{ fontSize: 18 }} />
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
              <MenuItem onClick={handleExportCSV} sx={{ fontSize: 13 }}>Export CSV</MenuItem>
              <MenuItem onClick={handleExportExcel} sx={{ fontSize: 13 }}>Export Excel</MenuItem>
            </Menu>
          </Box>

          <Button variant="outlined" startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={() => fetchData()} disabled={loading} size="small"
            sx={{ borderColor: TOOL_COLORS.REPORTS.primary, color: TOOL_COLORS.REPORTS.primary, fontSize: 12, fontWeight: 600, textTransform: 'none',
              '&:hover': { bgcolor: isDark ? 'rgba(47,196,178,0.1)' : 'rgba(47,196,178,0.08)', borderColor: TOOL_COLORS.REPORTS.hover } }}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset} size="small"
            sx={{ borderColor: theme.palette.divider, color: 'text.secondary', fontSize: 12, fontWeight: 600, textTransform: 'none',
              '&:hover': { borderColor: TOOL_COLORS.REPORTS.primary, color: TOOL_COLORS.REPORTS.primary } }}>
            Reset
          </Button>
        </Box>
      </Paper>

      {/* AI Insights Panel */}
      {filteredData.length > 0 && (
        <InsightsPanel filteredData={filteredData} getItemInfo={getItemInfo} dateFrom={dateFrom} dateTo={dateTo} source={source} />
      )}

      {/* AI Natural Language Query */}
      {filteredData.length > 0 && (
        <NLQueryBar filteredData={filteredData} getItemInfo={getItemInfo} dateFrom={dateFrom} dateTo={dateTo} />
      )}

      {/* Navigation Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: cardBg,
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(e, val) => { setCurrentTab(val); setSortCol(-1); if (val !== 6 && val !== 7 && viewMode === 'map') setViewMode('table'); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              fontSize: 13,
              fontWeight: 500,
              color: 'text.secondary',
              textTransform: 'none',
              minHeight: 48,
              px: '18px',
              '&.Mui-selected': {
                color: TOOL_COLORS.REPORTS.primary,
                fontWeight: 700,
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: TOOL_COLORS.REPORTS.primary,
              height: 3,
            },
          }}
        >
          {REPORT_TABS.map((label, idx) => (
            <Tab key={idx} label={label} />
          ))}
        </Tabs>
      </Paper>

      {/* Content */}
      <Box sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={36} sx={{ color: TOOL_COLORS.REPORTS.primary }} />
            <Typography sx={{ ml: 2, color: 'text.secondary' }}>Loading data...</Typography>
          </Box>
        )}
        {!loading && reportData && (
          <>
            {/* Title & Subtitle */}
            <Typography variant="h6" sx={{ fontSize: 18, color: TOOL_COLORS.REPORTS.primary, fontWeight: 600, mb: 0.5 }}>
              {reportData.title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
              {reportData.subtitle}
            </Typography>

            {/* Summary Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 1, md: 2.5 }, mb: 2 }}>
              <SummaryCard label="Total Quantity" value={fmtNum(reportData.totalQty)} theme={theme}
                icon={<Inventory2OutlinedIcon sx={{ fontSize: 20, color: TOOL_COLORS.REPORTS.primary, opacity: 0.7 }} />} />
              <SummaryCard label="Total Amount" value={fmtDollar(reportData.totalAmt)} theme={theme}
                icon={<AttachMoneyIcon sx={{ fontSize: 20, color: TOOL_COLORS.REPORTS.primary, opacity: 0.7 }} />} />
              <SummaryCard label="Unique Entries" value={fmtNum(reportData.totalRows)} theme={theme}
                icon={<FormatListBulletedIcon sx={{ fontSize: 20, color: TOOL_COLORS.REPORTS.primary, opacity: 0.7 }} />} />
              <SummaryCard label="Showing" value={fmtNum(displayedRows.length)} theme={theme}
                icon={<VisibilityOutlinedIcon sx={{ fontSize: 20, color: TOOL_COLORS.REPORTS.primary, opacity: 0.7 }} />} />
            </Box>

            {/* State Drill-Down Indicator */}
            {drillDownState && currentTab === 7 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip
                  label={`Drilling into: ${drillDownState}`}
                  onDelete={() => setDrillDownState(null)}
                  sx={{ fontWeight: 600, bgcolor: isDark ? 'rgba(156,142,164,0.15)' : 'rgba(156,142,164,0.1)', color: TOOL_COLORS.REPORTS.primary }}
                />
                <Typography variant="caption" color="text.secondary">Click a state on the map to drill down, or clear to see all</Typography>
              </Box>
            )}

            {/* DS vs Non-DS Comparison Bar */}
            {dsComparison.total > 0 && (
              <Paper elevation={0} sx={{
                border: `1px solid ${isDark ? 'rgba(47,196,178,0.2)' : 'rgba(47,196,178,0.15)'}`,
                borderRadius: 2,
                p: 2,
                mb: 2,
                bgcolor: isDark ? 'rgba(47,196,178,0.04)' : 'rgba(47,196,178,0.02)',
              }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                  DS vs Non-DS Comparison
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                  <Box sx={{ flex: 1, display: 'flex', borderRadius: 1, overflow: 'hidden', height: 24 }}>
                    {dsComparison.dsAmt > 0 && (
                      <MuiTooltip title={`DS: ${fmtDollar(dsComparison.dsAmt)} (${dsComparison.dsPct}%)`}>
                        <Box sx={{
                          width: `${dsComparison.dsPct}%`,
                          bgcolor: TOOL_COLORS.REPORTS.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'width 0.3s ease',
                          minWidth: dsComparison.dsAmt > 0 ? 40 : 0,
                        }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
                            DS {dsComparison.dsPct}%
                          </Typography>
                        </Box>
                      </MuiTooltip>
                    )}
                    {dsComparison.nonDsAmt > 0 && (
                      <MuiTooltip title={`Non-DS: ${fmtDollar(dsComparison.nonDsAmt)} (${dsComparison.nonDsPct}%)`}>
                        <Box sx={{
                          width: `${dsComparison.nonDsPct}%`,
                          bgcolor: '#6B8F71',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'width 0.3s ease',
                          minWidth: dsComparison.nonDsAmt > 0 ? 40 : 0,
                        }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
                            Non-DS {dsComparison.nonDsPct}%
                          </Typography>
                        </Box>
                      </MuiTooltip>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    DS: {fmtDollar(dsComparison.dsAmt)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    Non-DS: {fmtDollar(dsComparison.nonDsAmt)}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* View Mode Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, val) => { if (val) setViewMode(val); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600,
                    border: `1px solid ${theme.palette.divider}`,
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      bgcolor: TOOL_COLORS.REPORTS.primary,
                      color: 'white',
                      borderColor: TOOL_COLORS.REPORTS.primary,
                      '&:hover': { bgcolor: TOOL_COLORS.REPORTS.hover },
                    },
                    '&:hover': { bgcolor: isDark ? 'rgba(47,196,178,0.1)' : 'rgba(47,196,178,0.05)' },
                  },
                }}
              >
                <ToggleButton value="table">
                  <TableChartIcon sx={{ fontSize: 16, mr: 0.5 }} /> Table
                </ToggleButton>
                <ToggleButton value="chart">
                  <BarChartIcon sx={{ fontSize: 16, mr: 0.5 }} /> Chart
                </ToggleButton>
                <ToggleButton value="pie">
                  <PieChartOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} /> Pie
                </ToggleButton>
                {(currentTab === 6 || currentTab === 7) && (
                  <ToggleButton value="map">
                    <MapOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} /> Map
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </Box>

            {/* Bar Chart View */}
            {viewMode === 'chart' && (
              <Paper elevation={1} sx={{ borderRadius: 2, p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                  Top {Math.min(25, displayedRows.length)} items by Amount
                </Typography>
                {chartData.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No data to chart</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(400, chartData.length * 28)}>
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'} />
                      <XAxis
                        type="number"
                        tickFormatter={(v) => '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v.toFixed(0))}
                        tick={{ fill: isDark ? '#aaa' : '#666', fontSize: 11 }}
                        axisLine={{ stroke: isDark ? '#555' : '#ccc' }}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={isMobile ? 80 : 150}
                        tick={{ fill: isDark ? '#ccc' : '#444', fontSize: isMobile ? 10 : 11 }}
                        axisLine={{ stroke: isDark ? '#555' : '#ccc' }}
                      />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Bar
                        dataKey="Amount"
                        fill={TOOL_COLORS.REPORTS.primary}
                        radius={[0, 4, 4, 0]}
                        cursor="pointer"
                        onMouseEnter={() => {}}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            )}

            {/* Pie Chart View */}
            {viewMode === 'pie' && (
              <Paper elevation={1} sx={{ borderRadius: 2, p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                  Top {Math.min(10, displayedRows.length)} items by Amount
                </Typography>
                {pieData.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No data to chart</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={450}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={150}
                        innerRadius={60}
                        dataKey="value"
                        label={renderPieLabel}
                        labelLine={{ stroke: isDark ? '#888' : '#aaa' }}
                      >
                        {pieData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} cursor="pointer" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend
                        formatter={(value) => <span style={{ color: isDark ? '#ccc' : '#555', fontSize: 12 }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            )}

            {/* Map View — Warehouses (tab 6) */}
            {viewMode === 'map' && currentTab === 6 && (() => {
              const whsSales = {};
              filteredData.forEach(r => {
                if (!r.whs) return;
                if (!whsSales[r.whs]) whsSales[r.whs] = { qty: 0, amount: 0, orders: 0 };
                whsSales[r.whs].qty += r.qty;
                whsSales[r.whs].amount += r.amount;
                whsSales[r.whs].orders += 1;
              });
              const maxAmt = Math.max(...Object.values(whsSales).map(v => v.amount), 1);
              const sortedWhs = Object.entries(WAREHOUSE_LOCATIONS)
                .map(([code, loc]) => ({ code, ...loc, ...(whsSales[code] || { qty: 0, amount: 0, orders: 0 }) }))
                .sort((a, b) => b.amount - a.amount);
              const totalWhsAmt = sortedWhs.reduce((s, w) => s + w.amount, 0);

              return (
                <Paper elevation={1} sx={{ borderRadius: 2, p: 2, mb: 2, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: TOOL_COLORS.REPORTS.primary }}>
                    Warehouse Map — Sales by Location
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
                    Click warehouses to compare • Hover for details{selectedWarehouses.size > 0 ? ` • ${selectedWarehouses.size} selected` : ''}
                  </Typography>
                  {selectedWarehouses.size > 0 && (
                    <Button size="small" onClick={() => setSelectedWarehouses(new Set())}
                      sx={{ mb: 1, textTransform: 'none', fontSize: '0.7rem', color: TOOL_COLORS.REPORTS.primary }}>
                      Clear selection
                    </Button>
                  )}
                  <Box sx={{ bgcolor: isDark ? '#1a1a2e' : '#f0f4f8', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
                    <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
                      <ZoomableGroup>
                        <Geographies geography={US_TOPO_URL}>
                          {({ geographies }) => geographies.map(geo => {
                            const stName = geo.properties?.name || '';
                            const abbrev = STATE_NAME_TO_ABBREV[stName];
                            return (
                              <Geography key={geo.rpiKey || geo.id} geography={geo}
                                fill={isDark ? '#2a2a3e' : '#dce3ec'} stroke={isDark ? '#3a3a4e' : '#bcc5d0'} strokeWidth={0.5}
                                style={{ default: { outline: 'none' }, hover: { fill: isDark ? '#3a3a5e' : '#c5d0dd', outline: 'none' }, pressed: { outline: 'none' } }}
                                onMouseEnter={(e) => {
                                  if (abbrev) {
                                    setMapTooltip({ title: abbrev, subtitle: stName, rows: [] });
                                    setMapTooltipPos({ x: e.clientX, y: e.clientY });
                                  }
                                }}
                                onMouseMove={(e) => setMapTooltipPos({ x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => { setMapTooltip(null); setMapTooltipPos(null); }}
                              />
                            );
                          })}
                        </Geographies>
                        {sortedWhs.map(({ code, lat, lng, name, region, qty, amount, orders }) => {
                          const isSelected = selectedWarehouses.has(code);
                          const color = REGION_COLORS[region] || TOOL_COLORS.REPORTS.primary;
                          const pctOfTotal = totalWhsAmt > 0 ? ((amount / totalWhsAmt) * 100).toFixed(1) : '0.0';
                          return (
                            <Marker key={code} coordinates={[lng, lat]}>
                              <g style={{ cursor: 'pointer' }}
                                onClick={() => toggleWarehouse(code)}
                                onMouseEnter={(e) => {
                                  setMapTooltip({
                                    title: `${code} — ${name}`,
                                    subtitle: `Region: ${region}`,
                                    rows: [
                                      { label: 'Orders', value: fmtNum(orders) },
                                      { label: 'Qty', value: fmtNum(qty) },
                                      { label: 'Amount', value: fmtDollar(amount), color: '#90EE90' },
                                      { label: '% of Total', value: `${pctOfTotal}%` },
                                    ],
                                  });
                                  setMapTooltipPos({ x: e.clientX, y: e.clientY });
                                }}
                                onMouseMove={(e) => setMapTooltipPos({ x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => { setMapTooltip(null); setMapTooltipPos(null); }}
                              >
                                {/* Pin shape: pointed bottom marker */}
                                <path
                                  d="M0,-12 C-4,-12 -7,-9 -7,-5 C-7,-1 0,6 0,6 C0,6 7,-1 7,-5 C7,-9 4,-12 0,-12 Z"
                                  fill={isSelected ? '#fff' : color}
                                  stroke={isSelected ? color : '#fff'}
                                  strokeWidth={isSelected ? 2 : 1}
                                  fillOpacity={amount > 0 ? 0.95 : 0.35}
                                />
                                {/* Inner dot */}
                                <circle r={2.5} cy={-5.5} fill={isSelected ? color : '#fff'} fillOpacity={0.9} />
                              </g>
                              <text textAnchor="middle" y={14} style={{ fontSize: 6, fontWeight: 700, fill: isDark ? '#ccc' : '#444', fontFamily: 'sans-serif', pointerEvents: 'none' }}>{code}</text>
                            </Marker>
                          );
                        })}
                      </ZoomableGroup>
                    </ComposableMap>
                    <MapTooltip data={mapTooltip} position={mapTooltipPos} />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, justifyContent: 'center' }}>
                    {Object.entries(REGION_COLORS).filter(([k]) => k !== 'Central').map(([region, color]) => (
                      <Box key={region} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                        <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>{region}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Comparison panel */}
                  {selectedWarehouses.size >= 2 && (() => {
                    const selected = sortedWhs.filter(w => selectedWarehouses.has(w.code));
                    const totalSelOrders = selected.reduce((s, w) => s + w.orders, 0);
                    const totalSelQty = selected.reduce((s, w) => s + w.qty, 0);
                    const totalSelAmt = selected.reduce((s, w) => s + w.amount, 0);
                    const maxSelAmt = Math.max(...selected.map(w => w.amount), 1);
                    return (
                      <Paper elevation={2} sx={{ mt: 2, p: 2, border: `2px solid ${TOOL_COLORS.REPORTS.primary}`, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: TOOL_COLORS.REPORTS.primary }}>
                          Comparing {selected.length} Warehouses
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${Math.min(selected.length, 4)}, 1fr)` }, gap: { xs: 1, md: 2 }, mb: 2 }}>
                          {selected.map(w => (
                            <Paper key={w.code} elevation={0} sx={{ p: 1.5, border: `1px solid ${REGION_COLORS[w.region] || TOOL_COLORS.REPORTS.primary}`, borderRadius: 1, textAlign: 'center' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: REGION_COLORS[w.region] }}>{w.code}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10, mb: 1 }}>{w.name}</Typography>
                              <Box sx={{ width: '100%', height: 6, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', borderRadius: 3, mb: 1 }}>
                                <Box sx={{ width: `${(w.amount / maxSelAmt) * 100}%`, height: '100%', bgcolor: REGION_COLORS[w.region], borderRadius: 3 }} />
                              </Box>
                              <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{fmtDollar(w.amount)}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{fmtNum(w.orders)} orders · {fmtNum(w.qty)} units</Typography>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: TOOL_COLORS.REPORTS.primary }}>
                                {totalSelAmt > 0 ? ((w.amount / totalSelAmt) * 100).toFixed(1) : 0}% of selected
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Chip label={`Total: ${fmtDollar(totalSelAmt)}`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                          <Chip label={`${fmtNum(totalSelOrders)} orders`} size="small" sx={{ fontSize: 11 }} />
                          <Chip label={`${fmtNum(totalSelQty)} units`} size="small" sx={{ fontSize: 11 }} />
                          <Chip label={`Avg order: ${fmtDollar(totalSelOrders > 0 ? totalSelAmt / totalSelOrders : 0)}`} size="small" sx={{ fontSize: 11 }} />
                        </Box>
                      </Paper>
                    );
                  })()}

                  <Box sx={{ mt: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['WHS', 'Location', 'Region', 'Orders', 'Qty', 'Amount'].map(h => (
                            <TableCell key={h} sx={{ bgcolor: TOOL_COLORS.REPORTS.primary, color: '#fff', fontWeight: 600, fontSize: 11, py: 1 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedWhs.map(({ code, name, region, orders, qty, amount }) => (
                          <TableRow key={code} onClick={() => toggleWarehouse(code)} sx={{
                            cursor: 'pointer',
                            bgcolor: selectedWarehouses.has(code) ? (isDark ? 'rgba(47,196,178,0.15)' : 'rgba(47,196,178,0.1)') : 'inherit',
                            '&:hover': { bgcolor: isDark ? 'rgba(47,196,178,0.1)' : 'rgba(47,196,178,0.06)' },
                            borderLeft: selectedWarehouses.has(code) ? `3px solid ${REGION_COLORS[region] || TOOL_COLORS.REPORTS.primary}` : '3px solid transparent',
                          }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{code}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{name}</TableCell>
                            <TableCell><Chip label={region} size="small" sx={{ height: 20, fontSize: 10, bgcolor: REGION_COLORS[region] + '20', color: REGION_COLORS[region], fontWeight: 600 }} /></TableCell>
                            <TableCell align="right" sx={{ fontSize: 12 }}>{fmtNum(orders)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12 }}>{fmtNum(qty)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{fmtDollar(amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              );
            })()}

            {/* Map View — Zip Codes / States (tab 7) */}
            {viewMode === 'map' && currentTab === 7 && (() => {
              // Aggregate by state
              const stateSales = {};
              filteredData.forEach(r => {
                const st = (r.state || '').toUpperCase().trim();
                if (!st || st.length !== 2 || !STATE_COORDS[st]) return;
                if (!stateSales[st]) stateSales[st] = { qty: 0, amount: 0, orders: 0 };
                stateSales[st].qty += r.qty;
                stateSales[st].amount += r.amount;
                stateSales[st].orders += 1;
              });
              const maxAmt = Math.max(...Object.values(stateSales).map(v => v.amount), 1);
              const sortedStates = Object.entries(stateSales)
                .map(([st, v]) => ({ state: st, ...v }))
                .sort((a, b) => b.amount - a.amount);

              // Color scale: lighter to darker based on amount
              const getStateColor = (amt) => {
                const intensity = Math.max(0.15, Math.min(0.85, amt / maxAmt));
                return isDark
                  ? `rgba(156, 142, 164, ${intensity})`
                  : `rgba(156, 142, 164, ${0.1 + intensity * 0.7})`;
              };

              return (
                <Paper elevation={1} sx={{ borderRadius: 2, p: 2, mb: 2, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: TOOL_COLORS.REPORTS.primary }}>
                    Sales Heat Map by State
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
                    Darker = more sales • Bubble size = order volume • Hover for details
                  </Typography>
                  {/* Zip code aggregation for zip-level detail */}
                  {(() => {
                    const zipSales = {};
                    filteredData.forEach(r => {
                      const zip = (r.zip || '').trim();
                      const st = (r.state || '').toUpperCase().trim();
                      if (!zip || !st) return;
                      const key = `${zip}-${st}`;
                      if (!zipSales[key]) zipSales[key] = { zip, state: st, qty: 0, amount: 0, orders: 0 };
                      zipSales[key].qty += r.qty;
                      zipSales[key].amount += r.amount;
                      zipSales[key].orders += 1;
                    });
                    const topZips = Object.values(zipSales).sort((a, b) => b.amount - a.amount).slice(0, 10);
                    const totalStateAmt = sortedStates.reduce((s, e) => s + e.amount, 0);
                    const totalStateOrders = sortedStates.reduce((s, e) => s + e.orders, 0);
                    const avgOrderValue = totalStateOrders > 0 ? totalStateAmt / totalStateOrders : 0;

                    return (<>
                  <Box sx={{ bgcolor: isDark ? '#1a1a2e' : '#f0f4f8', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
                    <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
                      <ZoomableGroup>
                        <Geographies geography={US_TOPO_URL}>
                          {({ geographies }) => geographies.map(geo => {
                            const stName = geo.properties?.name || '';
                            const abbrev = STATE_NAME_TO_ABBREV[stName];
                            const sales = abbrev && stateSales[abbrev];
                            const fill = sales ? getStateColor(sales.amount) : (isDark ? '#2a2a3e' : '#dce3ec');
                            const pct = sales && totalStateAmt > 0 ? ((sales.amount / totalStateAmt) * 100).toFixed(1) : '0.0';
                            return (
                              <Geography key={geo.rpiKey || geo.id} geography={geo}
                                fill={fill} stroke={isDark ? '#3a3a4e' : '#bcc5d0'} strokeWidth={0.5}
                                style={{ default: { outline: 'none' }, hover: { fill: isDark ? '#5a4a6e' : '#9C8EA4', outline: 'none', strokeWidth: 1.5, cursor: 'pointer' }, pressed: { outline: 'none' } }}
                                onClick={() => { if (abbrev && sales) setDrillDownState(drillDownState === abbrev ? null : abbrev); }}
                                onMouseEnter={(e) => {
                                  setMapTooltip({
                                    title: abbrev || stName,
                                    subtitle: `${stName} — Click to drill down`,
                                    rows: sales ? [
                                      { label: 'Orders', value: fmtNum(sales.orders) },
                                      { label: 'Qty', value: fmtNum(sales.qty) },
                                      { label: 'Amount', value: fmtDollar(sales.amount), color: '#90EE90' },
                                      { label: '% of Total', value: `${pct}%` },
                                      { label: 'Avg Order', value: fmtDollar(sales.orders > 0 ? sales.amount / sales.orders : 0) },
                                    ] : [{ label: 'No shipments', value: '' }],
                                  });
                                  setMapTooltipPos({ x: e.clientX, y: e.clientY });
                                }}
                                onMouseMove={(e) => setMapTooltipPos({ x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => { setMapTooltip(null); setMapTooltipPos(null); }}
                              />
                            );
                          })}
                        </Geographies>
                        {sortedStates.slice(0, 30).map(({ state: st, qty, amount, orders }) => {
                          const coords = STATE_COORDS[st];
                          if (!coords) return null;
                          const size = Math.max(4, Math.min(18, (amount / maxAmt) * 18));
                          return (
                            <Marker key={st} coordinates={[coords[1], coords[0]]}>
                              <circle r={size} fill={TOOL_COLORS.REPORTS.primary} fillOpacity={0.7} stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer', pointerEvents: 'none' }} />
                              <text textAnchor="middle" y={3} style={{ fontSize: 7, fontWeight: 700, fill: '#fff', fontFamily: 'sans-serif', pointerEvents: 'none' }}>{st}</text>
                            </Marker>
                          );
                        })}
                      </ZoomableGroup>
                    </ComposableMap>
                    <MapTooltip data={mapTooltip} position={mapTooltipPos} />
                  </Box>
                  {/* Summary stats row */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, justifyContent: 'center' }}>
                    <Chip label={`${sortedStates.length} states`} size="small" sx={{ fontWeight: 600, fontSize: 11, bgcolor: TOOL_COLORS.REPORTS.primary + '20', color: TOOL_COLORS.REPORTS.primary }} />
                    <Chip label={`${fmtNum(totalStateOrders)} orders`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                    <Chip label={`${fmtDollar(totalStateAmt)} total`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                    <Chip label={`${fmtDollar(avgOrderValue)} avg order`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                  </Box>
                  {/* Color scale legend */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>Low</Typography>
                    <Box sx={{ width: 120, height: 8, borderRadius: 4, background: `linear-gradient(to right, rgba(47,196,178,0.1), rgba(47,196,178,0.85))` }} />
                    <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>High</Typography>
                  </Box>
                  {/* Top 10 zip codes mini table */}
                  {topZips.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: 12, color: TOOL_COLORS.REPORTS.primary }}>
                        Top 10 Zip Codes by Sales
                      </Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['#', 'Zip', 'State', 'Orders', 'Qty', 'Amount'].map(h => (
                                <TableCell key={h} sx={{ bgcolor: TOOL_COLORS.REPORTS.primary, color: '#fff', fontWeight: 600, fontSize: 11, py: 0.5 }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {topZips.map((z, idx) => (
                              <TableRow key={z.zip + z.state} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(47,196,178,0.1)' : 'rgba(47,196,178,0.06)' } }}>
                                <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{idx + 1}</TableCell>
                                <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>{z.zip}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{z.state}</TableCell>
                                <TableCell align="right" sx={{ fontSize: 11 }}>{fmtNum(z.orders)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: 11 }}>{fmtNum(z.qty)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>{fmtDollar(z.amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                    </>);
                  })()}
                  {/* Top states table */}
                  <Box sx={{ mt: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['#', 'State', 'Orders', 'Qty', 'Amount', '% of Total'].map(h => (
                            <TableCell key={h} sx={{ bgcolor: TOOL_COLORS.REPORTS.primary, color: '#fff', fontWeight: 600, fontSize: 11, py: 1 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedStates.slice(0, topN > 0 ? topN : sortedStates.length).map(({ state: st, orders, qty, amount }, idx) => {
                          const totalAmt = sortedStates.reduce((s, e) => s + e.amount, 0);
                          const pct = totalAmt > 0 ? ((amount / totalAmt) * 100).toFixed(1) : '0.0';
                          return (
                            <TableRow key={st} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(47,196,178,0.1)' : 'rgba(47,196,178,0.06)' } }}>
                              <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 12 }}>{idx + 1}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{st}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>{fmtNum(orders)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>{fmtNum(qty)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{fmtDollar(amount)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>{pct}%</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              );
            })()}

            {/* Data Table */}
            {viewMode === 'table' && (
              <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            bgcolor: TOOL_COLORS.REPORTS.primary,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            py: '10px',
                            px: '14px',
                          }}
                        >
                          #
                        </TableCell>
                        {reportData.headers.map((h, idx) => (
                          <TableCell
                            key={idx}
                            align={h.align === 'right' ? 'right' : 'left'}
                            onClick={h.sortable ? () => {
                              if (sortCol === idx) {
                                setSortDir(d => d === 'desc' ? 'asc' : 'desc');
                              } else {
                                setSortCol(idx);
                                setSortDir('asc');
                              }
                            } : undefined}
                            sx={{
                              bgcolor: TOOL_COLORS.REPORTS.primary,
                              color: 'white',
                              fontWeight: 600,
                              fontSize: 12,
                              whiteSpace: 'nowrap',
                              py: '10px',
                              px: '14px',
                              cursor: h.sortable ? 'pointer' : 'default',
                              userSelect: 'none',
                              '&:hover': h.sortable ? { bgcolor: TOOL_COLORS.REPORTS.hover } : {},
                            }}
                          >
                            {h.label}
                            {h.sortable && sortCol === idx && (
                              <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                            )}
                            {h.sortable && sortCol !== idx && (
                              <span style={{ marginLeft: 4, opacity: 0.4 }}>{'\u21C5'}</span>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={reportData.headers.length + 1}
                            sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
                          >
                            No data found
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayedRows.map((row, idx) => (
                          <TableRow
                            key={idx}
                            onClick={() => {
                              if (currentTab === 0 && row[0]) { setAiSelectedSku(row[0]); setAiSelectedCustomer(null); }
                              else if (currentTab === 1 && row[0]) { setAiSelectedCustomer(row[0]); setAiSelectedSku(null); }
                            }}
                            sx={{
                              cursor: (currentTab === 0 || currentTab === 1) ? 'pointer' : 'default',
                              '&:nth-of-type(even)': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(47,196,178,0.03)' },
                              '&:hover': { bgcolor: isDark ? 'rgba(47,196,178,0.1)' : 'rgba(47,196,178,0.08)' },
                              transition: 'background-color 0.15s ease',
                            }}
                          >
                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 13, px: '14px', py: '8px' }}>
                              {idx + 1}
                            </TableCell>
                            {row.map((cell, cellIdx) => (
                              <TableCell
                                key={cellIdx}
                                align={reportData.headers[cellIdx]?.align === 'right' ? 'right' : 'left'}
                                sx={{ fontSize: 13, px: '14px', py: '8px', borderBottom: '1px solid', borderColor: 'divider' }}
                              >
                                {cell}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}
          </>
        )}

        {/* AI Forecast Tab */}
        {currentTab === 9 && (
          <ForecastTab filteredData={filteredData} getItemInfo={getItemInfo} dateFrom={dateFrom} dateTo={dateTo} />
        )}

        {/* AI Customer Segmentation Tab */}
        {currentTab === 10 && (
          <CustomerSegmentTab filteredData={filteredData} dateFrom={dateFrom} dateTo={dateTo} />
        )}

        {/* AI Recommendations Panel (appears when clicking SKU or Customer row) */}
        {(aiSelectedSku || aiSelectedCustomer) && (currentTab === 0 || currentTab === 1) && (
          <RecommendationsPanel
            filteredData={filteredData}
            getItemInfo={getItemInfo}
            selectedSku={aiSelectedSku}
            selectedCustomer={aiSelectedCustomer}
            onClose={() => { setAiSelectedSku(null); setAiSelectedCustomer(null); }}
          />
        )}
      </Box>
    </Box>
  );
};

// Summary card sub-component
const SummaryCard = ({ label, value, theme, icon }) => {
  const isDark = theme.palette.mode === 'dark';
  const color = TOOL_COLORS.REPORTS.primary;
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        p: { xs: '10px 12px', md: '16px 22px' },
        minWidth: 0,
        border: `1px solid ${isDark ? 'rgba(47,196,178,0.2)' : 'rgba(47,196,178,0.15)'}`,
        background: isDark
          ? `linear-gradient(135deg, rgba(47,196,178,0.08) 0%, rgba(47,196,178,0.02) 100%)`
          : `linear-gradient(135deg, rgba(47,196,178,0.06) 0%, rgba(255,255,255,1) 100%)`,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${isDark ? 'rgba(47,196,178,0.2)' : 'rgba(47,196,178,0.15)'}`,
          borderColor: color,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            fontSize: 11,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        {icon && icon}
      </Box>
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 700,
          color,
          mt: 0.5,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
};

// ========== REPORT FUNCTIONS ==========

function reportTopSKUs(rows, search, getItemInfo, prevRows, days) {
  const map = {};
  for (const r of rows) {
    if (!r.itemId) continue;
    if (!map[r.itemId]) map[r.itemId] = { qty: 0, amount: 0 };
    map[r.itemId].qty += r.qty;
    map[r.itemId].amount += r.amount;
  }
  // Previous period for comparison
  const prevMap = {};
  if (prevRows) {
    for (const r of prevRows) {
      if (!r.itemId) continue;
      if (!prevMap[r.itemId]) prevMap[r.itemId] = { qty: 0, amount: 0 };
      prevMap[r.itemId].qty += r.qty;
      prevMap[r.itemId].amount += r.amount;
    }
  }
  let entries = Object.entries(map).map(([sku, v]) => {
    const info = getItemInfo(sku);
    const velocity = days > 0 ? (v.qty / days) : 0;
    const prev = prevMap[sku];
    const change = prev ? ((v.amount - prev.amount) / (prev.amount || 1)) * 100 : null;
    return { sku, collection: info.collection, qty: v.qty, amount: v.amount, velocity, change };
  });
  if (search) entries = entries.filter((e) => e.sku.toLowerCase().includes(search) || e.collection.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  const headers = [
    { label: 'SKU (Item ID)', align: 'left' },
    { label: 'Collection', align: 'left' },
    { label: 'Qty', align: 'right', sortable: true },
    { label: 'Units/Day', align: 'right', sortable: true },
    { label: 'Amount', align: 'right', sortable: true },
  ];
  if (prevRows) headers.push({ label: 'vs Prev', align: 'right', sortable: true });
  return {
    title: 'Top Selling SKUs',
    subtitle: `Sorted by Amount • Velocity = units/day over ${days} days${prevRows ? ' • vs Previous Period' : ''}`,
    headers,
    tableRows: entries.map((e) => {
      const row = [e.sku, e.collection, fmtNum(e.qty), e.velocity.toFixed(1), fmtDollar(e.amount)];
      if (prevRows) row.push(e.change !== null ? `${e.change >= 0 ? '+' : ''}${e.change.toFixed(1)}%` : 'NEW');
      return row;
    }),
    rawData: entries.map((e) => {
      const row = [null, null, e.qty, e.velocity, e.amount];
      if (prevRows) row.push(e.change ?? 999);
      return row;
    }),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportTopCustomers(rows, search, prevRows) {
  const map = {};
  for (const r of rows) {
    if (!r.customer) continue;
    if (!map[r.customer]) map[r.customer] = { qty: 0, amount: 0 };
    map[r.customer].qty += r.qty;
    map[r.customer].amount += r.amount;
  }
  const prevMap = {};
  if (prevRows) {
    for (const r of prevRows) {
      if (!r.customer) continue;
      if (!prevMap[r.customer]) prevMap[r.customer] = { qty: 0, amount: 0 };
      prevMap[r.customer].qty += r.qty;
      prevMap[r.customer].amount += r.amount;
    }
  }
  let entries = Object.entries(map).map(([c, v]) => {
    const prev = prevMap[c];
    const change = prev ? ((v.amount - prev.amount) / (prev.amount || 1)) * 100 : null;
    return { customer: c, qty: v.qty, amount: v.amount, change };
  });
  if (search) entries = entries.filter((e) => e.customer.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  const headers = [
    { label: 'Customer', align: 'left' },
    { label: 'Units', align: 'right', sortable: true },
    { label: 'Amount', align: 'right', sortable: true },
  ];
  if (prevRows) headers.push({ label: 'vs Prev', align: 'right', sortable: true });
  return {
    title: 'Top Selling Customers',
    subtitle: prevRows ? 'Sorted by Amount • vs Previous Period' : 'Sorted by Amount ($$)',
    headers,
    tableRows: entries.map((e) => {
      const row = [e.customer, fmtNum(e.qty), fmtDollar(e.amount)];
      if (prevRows) row.push(e.change !== null ? `${e.change >= 0 ? '+' : ''}${e.change.toFixed(1)}%` : 'NEW');
      return row;
    }),
    rawData: entries.map((e) => {
      const row = [null, e.qty, e.amount];
      if (prevRows) row.push(e.change ?? 999);
      return row;
    }),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportTopCollections(rows, search, getItemInfo, prevRows) {
  const map = {};
  for (const r of rows) {
    const info = getItemInfo(r.itemId);
    const key = info.collection || '(Unknown)';
    if (!map[key]) map[key] = { qty: 0, amount: 0 };
    map[key].qty += r.qty;
    map[key].amount += r.amount;
  }
  let entries = Object.entries(map).map(([c, v]) => ({ collection: c, qty: v.qty, amount: v.amount }));
  if (search) entries = entries.filter((e) => e.collection.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  return {
    title: 'Top Selling Collections',
    subtitle: 'Sorted by Amount ($$)',
    headers: [
      { label: 'Collection', align: 'left' },
      { label: 'Quantity', align: 'right', sortable: true },
      { label: 'Amount', align: 'right', sortable: true },
    ],
    tableRows: entries.map((e) => [e.collection, fmtNum(e.qty), fmtDollar(e.amount)]),
    rawData: entries.map((e) => [null, e.qty, e.amount]),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportTopSizes(rows, search, getItemInfo, prevRows) {
  const map = {};
  for (const r of rows) {
    const info = getItemInfo(r.itemId);
    const key = info.size || '(Unknown)';
    // Exclude any size containing "1x1"
    const norm = key.toLowerCase().replace(/[^0-9x]/g, '');
    if (norm === '1x1' || norm.startsWith('1x1')) continue;
    if (!map[key]) map[key] = { qty: 0, amount: 0 };
    map[key].qty += r.qty;
    map[key].amount += r.amount;
  }
  let entries = Object.entries(map).map(([s, v]) => ({ size: s, qty: v.qty, amount: v.amount }));
  if (search) entries = entries.filter((e) => e.size.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  return {
    title: 'Top Selling Sizes',
    subtitle: 'Sorted by Amount ($$) \u2014 Excludes 1x1',
    headers: [
      { label: 'Size', align: 'left' },
      { label: 'Quantity', align: 'right', sortable: true },
      { label: 'Amount', align: 'right', sortable: true },
    ],
    tableRows: entries.map((e) => [e.size, fmtNum(e.qty), fmtDollar(e.amount)]),
    rawData: entries.map((e) => [null, e.qty, e.amount]),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportTopColors(rows, search, getItemInfo, prevRows) {
  const map = {};
  for (const r of rows) {
    const info = getItemInfo(r.itemId);
    const key = info.color || '(Unknown)';
    if (!map[key]) map[key] = { qty: 0, amount: 0 };
    map[key].qty += r.qty;
    map[key].amount += r.amount;
  }
  let entries = Object.entries(map).map(([c, v]) => ({ color: c, qty: v.qty, amount: v.amount }));
  if (search) entries = entries.filter((e) => e.color.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  return {
    title: 'Top Selling Colors',
    subtitle: 'Sorted by Amount ($$)',
    headers: [
      { label: 'Color', align: 'left' },
      { label: 'Quantity', align: 'right', sortable: true },
      { label: 'Amount', align: 'right', sortable: true },
    ],
    tableRows: entries.map((e) => [e.color, fmtNum(e.qty), fmtDollar(e.amount)]),
    rawData: entries.map((e) => [null, e.qty, e.amount]),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportTopStyles(rows, search, getItemInfo, prevRows) {
  const map = {};
  for (const r of rows) {
    const info = getItemInfo(r.itemId);
    const key = info.style || '(Unknown)';
    if (!map[key]) map[key] = { qty: 0, amount: 0 };
    map[key].qty += r.qty;
    map[key].amount += r.amount;
  }
  let entries = Object.entries(map).map(([s, v]) => ({ style: s, qty: v.qty, amount: v.amount }));
  if (search) entries = entries.filter((e) => e.style.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  return {
    title: 'Top Selling Styles',
    subtitle: 'Sorted by Amount ($$)',
    headers: [
      { label: 'Style', align: 'left' },
      { label: 'Quantity', align: 'right', sortable: true },
      { label: 'Amount', align: 'right', sortable: true },
    ],
    tableRows: entries.map((e) => [e.style, fmtNum(e.qty), fmtDollar(e.amount)]),
    rawData: entries.map((e) => [null, e.qty, e.amount]),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportTopWarehouses(rows, search, days, prevRows) {
  const map = {};
  for (const r of rows) {
    const key = r.whs || '(Unknown)';
    if (!map[key]) map[key] = { qty: 0, amount: 0, orders: 0 };
    map[key].qty += r.qty;
    map[key].amount += r.amount;
    map[key].orders += 1;
  }
  const prevMap = {};
  if (prevRows) {
    for (const r of prevRows) {
      const key = r.whs || '(Unknown)';
      if (!prevMap[key]) prevMap[key] = { qty: 0, amount: 0 };
      prevMap[key].qty += r.qty;
      prevMap[key].amount += r.amount;
    }
  }
  let entries = Object.entries(map).map(([w, v]) => {
    const avgOrderVal = v.orders > 0 ? v.amount / v.orders : 0;
    const itemsPerOrder = v.orders > 0 ? v.qty / v.orders : 0;
    const ordersPerDay = days > 0 ? v.orders / days : 0;
    const prev = prevMap[w];
    const change = prev ? ((v.amount - prev.amount) / (prev.amount || 1)) * 100 : null;
    return { whs: w, qty: v.qty, amount: v.amount, orders: v.orders, avgOrderVal, itemsPerOrder, ordersPerDay, change };
  });
  if (search) entries = entries.filter((e) => e.whs.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  const headers = [
    { label: 'WHS', align: 'left' },
    { label: 'Orders', align: 'right', sortable: true },
    { label: 'Qty', align: 'right', sortable: true },
    { label: 'Amount', align: 'right', sortable: true },
    { label: 'Avg Order $', align: 'right', sortable: true },
    { label: 'Items/Order', align: 'right', sortable: true },
    { label: 'Orders/Day', align: 'right', sortable: true },
  ];
  if (prevRows) headers.push({ label: 'vs Prev', align: 'right', sortable: true });
  return {
    title: 'Top Shipping Warehouses',
    subtitle: `Efficiency Metrics over ${days} days${prevRows ? ' • vs Previous Period' : ''}`,
    headers,
    tableRows: entries.map((e) => {
      const row = [e.whs, fmtNum(e.orders), fmtNum(e.qty), fmtDollar(e.amount), fmtDollar(e.avgOrderVal), e.itemsPerOrder.toFixed(1), e.ordersPerDay.toFixed(1)];
      if (prevRows) row.push(e.change !== null ? `${e.change >= 0 ? '+' : ''}${e.change.toFixed(1)}%` : 'NEW');
      return row;
    }),
    rawData: entries.map((e) => {
      const row = [null, e.orders, e.qty, e.amount, e.avgOrderVal, e.itemsPerOrder, e.ordersPerDay];
      if (prevRows) row.push(e.change ?? 999);
      return row;
    }),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportTopZips(rows, search, prevRows, drillDownState) {
  // If drilling into a state, filter rows to only that state
  let filtered = rows;
  if (drillDownState) {
    filtered = rows.filter(r => (r.state || '').toUpperCase() === drillDownState);
  }
  const map = {};
  for (const r of filtered) {
    const key = r.zip ? r.zip + ' (' + r.state + ')' : '(Unknown)';
    if (!map[key]) map[key] = { qty: 0, amount: 0, orders: 0 };
    map[key].qty += r.qty;
    map[key].amount += r.amount;
    map[key].orders += 1;
  }
  const prevMap = {};
  if (prevRows) {
    const prevFiltered = drillDownState ? prevRows.filter(r => (r.state || '').toUpperCase() === drillDownState) : prevRows;
    for (const r of prevFiltered) {
      const key = r.zip ? r.zip + ' (' + r.state + ')' : '(Unknown)';
      if (!prevMap[key]) prevMap[key] = { qty: 0, amount: 0 };
      prevMap[key].qty += r.qty;
      prevMap[key].amount += r.amount;
    }
  }
  let entries = Object.entries(map).map(([z, v]) => {
    const prev = prevMap[z];
    const change = prev ? ((v.amount - prev.amount) / (prev.amount || 1)) * 100 : null;
    return { zip: z, qty: v.qty, amount: v.amount, orders: v.orders, change };
  });
  if (search) entries = entries.filter((e) => e.zip.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  const headers = [
    { label: 'Zip Code (State)', align: 'left' },
    { label: 'Orders', align: 'right', sortable: true },
    { label: 'Quantity', align: 'right', sortable: true },
    { label: 'Amount', align: 'right', sortable: true },
  ];
  if (prevRows) headers.push({ label: 'vs Prev', align: 'right', sortable: true });
  return {
    title: drillDownState ? `Zip Codes in ${drillDownState}` : 'Top Shipping to Zip Code',
    subtitle: drillDownState ? `Filtered to ${drillDownState} — Click state on map to change` : 'Includes State \u2014 Sorted by Amount ($$)',
    headers,
    tableRows: entries.map((e) => {
      const row = [e.zip, fmtNum(e.orders), fmtNum(e.qty), fmtDollar(e.amount)];
      if (prevRows) row.push(e.change !== null ? `${e.change >= 0 ? '+' : ''}${e.change.toFixed(1)}%` : 'NEW');
      return row;
    }),
    rawData: entries.map((e) => {
      const row = [null, e.orders, e.qty, e.amount];
      if (prevRows) row.push(e.change ?? 999);
      return row;
    }),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

function reportItemsByZip(rows, search, getItemInfo) {
  const map = {};
  for (const r of rows) {
    const zipKey = r.zip ? r.zip + ' (' + r.state + ')' : '(Unknown)';
    const key = zipKey + '|||' + r.itemId;
    if (!map[key]) map[key] = { zip: zipKey, itemId: r.itemId, qty: 0, amount: 0 };
    map[key].qty += r.qty;
    map[key].amount += r.amount;
  }
  let entries = Object.values(map);
  if (search) entries = entries.filter((e) => e.zip.toLowerCase().includes(search) || e.itemId.toLowerCase().includes(search));
  entries.sort((a, b) => b.amount - a.amount);
  const totalQty = entries.reduce((s, e) => s + e.qty, 0);
  const totalAmt = entries.reduce((s, e) => s + e.amount, 0);
  return {
    title: 'Top Items Shipped by Zip Code',
    subtitle: 'Sorted by Amount ($$)',
    headers: [
      { label: 'Zip Code (State)', align: 'left' },
      { label: 'Item ID', align: 'left' },
      { label: 'Collection', align: 'left' },
      { label: 'Quantity', align: 'right', sortable: true },
      { label: 'Amount', align: 'right', sortable: true },
    ],
    tableRows: entries.map((e) => {
      const info = getItemInfo(e.itemId);
      return [e.zip, e.itemId, info.collection, fmtNum(e.qty), fmtDollar(e.amount)];
    }),
    rawData: entries.map((e) => [null, null, null, e.qty, e.amount]),
    totalQty,
    totalAmt,
    totalRows: entries.length,
  };
}

export default SalesDashboard;
